import { isEqual, pick } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

import { SRPRS } from 'components/_Assets/svg/Icon/srprs'
import { ActionList } from 'components/ActionList'
import { BlockItem, BlockSelect, BlockSelectType } from 'components/BlockSelect'
import Box, { BoxType } from 'components/Box'
import { Button, TYPE } from 'components/Button'
import Heading from 'components/Heading'
import Hero from 'components/Hero'
import { HeroType } from 'components/Hero/Hero'
import { InfoBox, InfoBoxItem } from 'components/InfoBox'
import ListHeader from 'components/ListHeader'
import Photo from 'components/Photo'
import Section from 'components/Section'
import { SectionType, Size } from 'components/Section/Section'
import Style from 'components/Style'
import { Timeline, TimelineItem } from 'components/Timeline'
import { getCdnUrl } from '../../data/api/cdn'
import { EntryProps } from '../../data/entries/types'
import { Theme } from '../../data/models/funnelOrder/theme'
import {
  getSiteCountryICC2,
  getSiteCurrencyISO,
  SiteCountry,
  SiteCurrency,
} from '../../data/models/locale'
import { getClient } from '../../data/openapi'
import { AppState } from '../../data/state'
import { FunnelStep } from '../../data/ui'
import Calendar from '../../elements/Calendar'
import FunnelFooter from '../../elements/footers/funnelFooter'
import FunnelBreadcrumbs from '../../elements/FunnelBreadcrumbs'
import GiftcardModal from '../../elements/modals/GiftcardModal'
import PersonModal from '../../elements/modals/PersonModal'
import TextTooltip from '../../elements/tooltips/textTooltip'
import { getNameWithTheme, getParamsWithTheme } from '../../routing/links'
import FlyExcluded from '../funnel/customise/options/flyExcluded'
import People from '../funnel/customise/options/people'
import { QueryKey } from '../funnel/customise/wrapper'
import {
  CustomiseWrapperInnerProps,
  withAirportsAndPrices,
} from '../funnel/customise/wrapperFunction'
import DisplayContent from '../shared/content'
import Page from '../shared/page_wrapper'
import BasicInfo from './components/basicInfo'
import HowItWorks from './components/entries/howItWorks'
import FAQ from './components/faq'
import FlavourBox from './components/flavourBox'

interface SummerHolidayProps extends EntryProps {
  patternImage: string
  total: number
  days: number
  country: SiteCountry
  currency: SiteCurrency
  contentLoading: boolean
  onChangeDays: (days: number) => void
}

interface SummerHolidayState {
  five_days: number
  seven_days: number
}

class SummerHolidayPage extends React.Component<
  SummerHolidayProps & CustomiseWrapperInnerProps,
  SummerHolidayState
> {
  private infoSection?: HTMLDivElement
  private detailsSection?: HTMLDivElement
  constructor(props: SummerHolidayProps & CustomiseWrapperInnerProps) {
    super(props)
    this.state = {
      five_days: NaN,
      seven_days: NaN,
    }
  }

  public shouldPricesUpdate(
    oldProps: SummerHolidayProps & CustomiseWrapperInnerProps,
  ) {
    const keys = ['currency', 'country', 'contentLoading']

    return !isEqual(pick(oldProps, keys), pick(this.props, keys))
  }

  public componentDidUpdate(
    oldProps: SummerHolidayProps & CustomiseWrapperInnerProps,
  ) {
    if (this.shouldPricesUpdate(oldProps)) {
      if (!this.props.contentLoading) {
        const { country, currency } = this.props
        const data = {
          site_country: getSiteCountryICC2(country),
          site_currency: getSiteCurrencyISO(currency),
        }
        getClient()
          .then(client => client.apis.bookings.bookings_inner_from_prices(data))
          .then(response => {
            const state = {
              five_days: response.body.five_days.price,
              seven_days: response.body.seven_days.price,
            }
            this.setState(state)
          })
      }
    }
  }

  public render() {
    const {
      entry,
      match,
      prices,
      loading,
      currency,
      patternImage,
      days,
    } = this.props

    if (entry.page === undefined) {
      throw new Error('No page properties')
    }

    return (
      <Page
        match={match}
        page={entry.page.name}
        color={entry.colour}
        navBarType="funnel"
        navBarOverrideColour={
          entry.page.style ? entry.page.style.navbarColor : undefined
        }
        navFooter
      >
        <Hero
          blurMobile
          type={HeroType.flavourEntry}
          headingColor={
            entry.page.style ? entry.page.style.headingColor : undefined
          }
          headingText={
            <DisplayContent contentKey="title">{entry.name}</DisplayContent>
          }
          subHeading={<FunnelBreadcrumbs funnelStep={FunnelStep.Customise} />}
          src={entry.page.photos.src}
          src1024={entry.page.photos.src1024}
          src1280={entry.page.photos.src1280}
          src1600={entry.page.photos.src1600}
          src1920={entry.page.photos.src1920}
        >
          <FunnelBreadcrumbs funnelStep={FunnelStep.Customise} />
          <Heading level={1}>
            <DisplayContent contentKey="title">{entry.name}</DisplayContent>
          </Heading>
          <InfoBox>
            <InfoBoxItem
              icon={SRPRS.calendarColored()}
              firstLine={
                <DisplayContent contentKey="1-info-top">3 - 14</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="1-info-bottom">
                  Dagen
                </DisplayContent>
              }
            />
            <InfoBoxItem
              icon={
                entry.theme === Theme.fly
                  ? SRPRS.aeroplaneColored
                  : SRPRS.carColored
              }
              firstLine={
                <DisplayContent contentKey="2-info-top">2 tot 4</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="2-info-bottom">
                  Steden
                </DisplayContent>
              }
            />
            <InfoBoxItem
              icon={SRPRS.mapMarkerColored}
              iconProps={{
                width: '37',
                height: '37',
                viewBox: '0 0 37 41',
                fill: 'none',
              }}
              firstLine={
                <DisplayContent contentKey="3-info-top">Heel</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="3-info-bottom">
                  Europa
                </DisplayContent>
              }
            />
          </InfoBox>
        </Hero>
        <BasicInfo
          detailsScroll={this.handleDetailsScroll}
          infoScroll={this.handleInfoScroll}
          nrItems={4}
        />
        <Section type={SectionType.solid} size={Size.s} noPadding>
          <Style margin={[[40, 0, 20, 0]]}>
            <div ref={this.setDetailsRef} />
            <Timeline direction="vertical" numbers>
              <TimelineItem>
                <Heading level={3}>
                  <DisplayContent contentKey="type-heading">
                    Choose your ideal srrps.summerholiday?
                  </DisplayContent>
                </Heading>
                <BlockSelect
                  model="days"
                  nrCols={[1, 2]}
                  type={BlockSelectType.card}
                  value={days}
                  onChange={this.changeDays}
                >
                  <BlockItem
                    label="City Escape"
                    value={5}
                    labelSubline={
                      <>
                        <Style margin={[[0, 0, 10, 0]]}>
                          <DisplayContent contentKey="relax-blurb">
                            5 days relaxing on the beach with a cocktail in hand
                          </DisplayContent>
                        </Style>
                        <DisplayContent contentKey="relax-story-1">
                          5 days
                        </DisplayContent>
                        <br />
                        <DisplayContent contentKey="relax-story-2">
                          1 accomodation
                        </DisplayContent>
                        <br />
                        <DisplayContent contentKey="relax-story-3">
                          Seaside destination
                        </DisplayContent>
                      </>
                    }
                    footerText={
                      <DisplayContent
                        contentKey="relax-from"
                        vars={{
                          currency,
                          price: this.renderPrice(this.state.five_days),
                        }}
                      >
                        {`From ${currency} ${this.renderPrice(
                          this.state.five_days,
                        )} p.p`}
                      </DisplayContent>
                    }
                    footerButton={
                      <TextTooltip
                        contentKey="roomType-budget-info-content"
                        placeholder="Shared accomodation info"
                      >
                        <Button type={TYPE.info} negative={true} />
                      </TextTooltip>
                    }
                    illustration={
                      <Photo
                        alt="Blam!"
                        src={getCdnUrl('Blam2x.jpg')}
                        backgroundImage
                      />
                    }
                  />
                  <BlockItem
                    label="Island Escape"
                    value={7}
                    labelSubline={
                      <>
                        <Style margin={[[0, 0, 10, 0]]}>
                          <DisplayContent contentKey="explore-blurb">
                            Discover a city for 3 days and then relax on the
                            beach for 4 days
                          </DisplayContent>
                        </Style>
                        <DisplayContent contentKey="explore-story-1">
                          7 days
                        </DisplayContent>
                        <br />
                        <DisplayContent contentKey="explore-story-2">
                          2 accomodations
                        </DisplayContent>
                        <br />
                        <DisplayContent contentKey="explore-story-3">
                          City and Sea
                        </DisplayContent>
                      </>
                    }
                    footerText={
                      <DisplayContent
                        contentKey="explore-from"
                        vars={{
                          currency,
                          price: this.renderPrice(this.state.seven_days),
                        }}
                      >
                        {`From ${currency} ${this.renderPrice(
                          this.state.seven_days,
                        )} p.p.`}
                      </DisplayContent>
                    }
                    footerButton={
                      <TextTooltip
                        contentKey="explore-info-content"
                        placeholder="Shared accomodation info"
                      >
                        <Button type={TYPE.info} negative={true} />
                      </TextTooltip>
                    }
                    illustration={
                      <Photo
                        alt="Pop art talk"
                        src={getCdnUrl('Pop-art_talk2x.jpg')}
                        backgroundImage
                      />
                    }
                  />
                </BlockSelect>
              </TimelineItem>
              <TimelineItem>
                <div className="margin-b-m">
                  <ActionList>
                    <People />
                    <FlyExcluded />
                  </ActionList>
                </div>
                <Box fullWidthMobile>
                  <Calendar
                    actionList
                    hideTrigger
                    showCheapest={true}
                    prices={prices}
                    loading={loading}
                  />
                </Box>
              </TimelineItem>
            </Timeline>
          </Style>
          <PersonModal />
        </Section>
        <FunnelFooter
          nextStepNo={2}
          nextStepLink={getNameWithTheme('extras')}
          nextStepLinkParams={getParamsWithTheme()}
          nextStepName={
            <DisplayContent edit={true} contentKey="extras">
              Extras
            </DisplayContent>
          }
          stickyStop
          nextStepDisabled={isNaN(this.props.total)}
        />
        <GiftcardModal />
        <FlavourBox
          patternImage={patternImage}
          setInfoRef={this.setInfoRef}
          content={entry.page.content}
        />
        <HowItWorks boxTypeColor={BoxType.golden} />
        <Section type={SectionType.white}>
          <ListHeader noMarginBottom>
            <Button
              cutOff="none"
              type={TYPE.plain}
              onClick={this.handleDetailsScroll}
            >
              <DisplayContent contentKey="boek-direct" edit={true}>
                Boek direct
              </DisplayContent>
            </Button>
          </ListHeader>
        </Section>
        <FAQ nrItems={5} />
      </Page>
    )
  }

  private changeDays = (days: string) => {
    this.props.onChangeDays(+days)
  }

  private renderPrice = (price: number) => {
    return isNaN(price) ? '-' : price.toString()
  }

  private setInfoRef = (ref: HTMLDivElement) => {
    this.infoSection = ref
  }

  private setDetailsRef = (ref: HTMLDivElement) => {
    this.detailsSection = ref
  }

  private handleInfoScroll = () => {
    if (this.infoSection) {
      this.infoSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  private handleDetailsScroll = () => {
    if (this.detailsSection) {
      this.detailsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

const ConnectedSummerHolidayPage = connect(
  (state: AppState) => ({
    patternImage: state.page.patternImage,
    total: state.funnelOrder.total,
    days: state.funnelOrder.days,
    country: state.locale.country,
    currency: state.locale.currency,
    contentLoading: state.contentEngine.loading,
  }),
  dispatch => ({
    onChangeDays: (days: number) => {
      dispatch(actions.change('funnelOrder.days', days))
    },
  }),
)(SummerHolidayPage)

export default withAirportsAndPrices<EntryProps>(ConnectedSummerHolidayPage, [
  QueryKey.month,
  QueryKey.year,
])
